/**
 * Touch gesture utilities for mobile interactions
 * Reusable swipe detection for Svelte components
 */

export interface SwipeOptions {
	threshold?: number;
	onSwipeLeft?: () => void;
	onSwipeRight?: () => void;
	onSwipeUp?: () => void;
	onSwipeDown?: () => void;
	// Visual feedback during swipe
	onSwipeMove?: (deltaX: number, deltaY: number) => void;
	// Edge swipe detection (swipe from right edge to close)
	edgeWidth?: number;
	allowEdgeSwipe?: boolean;
}

export interface SwipeHandler {
	destroy: () => void;
}

export function createSwipeHandler(
	element: HTMLElement,
	options: SwipeOptions
): SwipeHandler {
	let startX = 0;
	let startY = 0;
	let isSwiping = false;
	const threshold = options.threshold ?? 50;

	function onTouchStart(e: TouchEvent) {
		const touch = e.touches[0];
		startX = touch.clientX;
		startY = touch.clientY;
		isSwiping = true;
	}

	function onTouchMove(e: TouchEvent) {
		if (!isSwiping) return;

		const touch = e.touches[0];
		const diffX = touch.clientX - startX;
		const diffY = touch.clientY - startY;

		// Call move callback for visual feedback
		if (options.onSwipeMove) {
			options.onSwipeMove(diffX, diffY);
		}
	}

	function onTouchEnd(e: TouchEvent) {
		if (!isSwiping) return;
		isSwiping = false;

		const endX = e.changedTouches[0].clientX;
		const endY = e.changedTouches[0].clientY;
		const diffX = endX - startX;
		const diffY = endY - startY;

		// Determine if horizontal or vertical based on which distance is greater
		if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > threshold) {
			// Horizontal swipe
			if (diffX < 0) options.onSwipeLeft?.();
			else options.onSwipeRight?.();
		} else if (Math.abs(diffY) > Math.abs(diffX) && Math.abs(diffY) > threshold) {
			// Vertical swipe
			if (diffY < 0) options.onSwipeUp?.();
			else options.onSwipeDown?.();
		}
	}

	element.addEventListener('touchstart', onTouchStart, { passive: true });
	element.addEventListener('touchmove', onTouchMove, { passive: true });
	element.addEventListener('touchend', onTouchEnd, { passive: true });

	return {
		destroy() {
			element.removeEventListener('touchstart', onTouchStart);
			element.removeEventListener('touchmove', onTouchMove);
			element.removeEventListener('touchend', onTouchEnd);
		}
	};
}

/**
 * Create a swipeable drawer/sidebar handler
 * Specifically designed for mobile drawers that can be swiped to close
 */
export interface DrawerSwipeOptions {
	element: HTMLElement;
	onClose?: () => void;
	onOpen?: () => void;
	threshold?: number;
	edgeWidth?: number;
	// Apply visual transform during swipe
	onTransform?: (transform: string) => void;
}

export function createDrawerSwipeHandler(options: DrawerSwipeOptions): SwipeHandler {
	const { element, onClose, threshold = 80, edgeWidth = 40, onTransform } = options;

	let startX = 0;
	let startY = 0;
	let isSwiping = false;
	let isEdgeSwipe = false;
	let hasTransform = false;

	function onTouchStart(e: TouchEvent) {
		const touch = e.touches[0];
		startX = touch.clientX;
		startY = touch.clientY;
		isSwiping = true;

		// Check if starting from right edge
		const rect = element.getBoundingClientRect();
		const relativeX = touch.clientX - rect.left;
		isEdgeSwipe = relativeX > rect.width - edgeWidth;
	}

	function onTouchMove(e: TouchEvent) {
		if (!isSwiping) return;

		const touch = e.touches[0];
		const diffX = touch.clientX - startX;
		const diffY = touch.clientY - startY;

		// If vertical movement is greater, assume scrolling - abort swipe
		if (Math.abs(diffY) > Math.abs(diffX)) {
			isSwiping = false;
			if (hasTransform && onTransform) {
				onTransform('');
				hasTransform = false;
			}
			return;
		}

		// Allow swipe left from anywhere, swipe right only from edge
		if (diffX < 0 || (diffX > 0 && isEdgeSwipe)) {
			const clampedX = Math.min(diffX, 0); // Only negative (left) values
			if (onTransform) {
				onTransform(`translateX(${clampedX}px)`);
				hasTransform = true;
			}
		}
	}

	function onTouchEnd(e: TouchEvent) {
		if (!isSwiping) return;
		isSwiping = false;

		const endX = e.changedTouches[0].clientX;
		const diffX = endX - startX;

		// Reset transform
		if (hasTransform && onTransform) {
			onTransform('');
			hasTransform = false;
		}

		// Close if swiped left past threshold
		if (diffX < -threshold && onClose) {
			onClose();
		}
	}

	element.addEventListener('touchstart', onTouchStart, { passive: true });
	element.addEventListener('touchmove', onTouchMove, { passive: true });
	element.addEventListener('touchend', onTouchEnd, { passive: true });

	return {
		destroy() {
			element.removeEventListener('touchstart', onTouchStart);
			element.removeEventListener('touchmove', onTouchMove);
			element.removeEventListener('touchend', onTouchEnd);
		}
	};
}

/**
 * Body scroll lock for modals/drawers
 * Prevents background scrolling when an overlay is open
 * Uses direct style manipulation to avoid touch-action inheritance issues
 */
let lockedScrollY = 0;

export function lockBodyScroll() {
	if (typeof window === 'undefined') return;
	lockedScrollY = window.scrollY;
	document.body.style.position = 'fixed';
	document.body.style.top = `-${lockedScrollY}px`;
	document.body.style.width = '100%';
	document.body.style.overflow = 'hidden';
}

export function unlockBodyScroll() {
	if (typeof window === 'undefined') return;
	document.body.style.position = '';
	document.body.style.top = '';
	document.body.style.width = '';
	document.body.style.overflow = '';
	window.scrollTo(0, lockedScrollY);
}

